import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import PresetService, {
  GalleryItem,
} from "@/features/packs/services/presets/presetService";
import { QueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/model/AuthContext";
import { MAX_FILE_SIZE_BYTES, getFileSizeErrorMessage } from "@/shared/lib/fileUploadConstants";
const queryClient = new QueryClient();

// Extended interface for local gallery items with file references
interface LocalGalleryItem extends GalleryItem {
  beforeFile?: File;
  afterFile?: File;
}

export const useGalleryOperations = (
  presetId: string | null,
  isEditing: boolean = false
) => {
  const [galleryItems, setGalleryItems] = useState<LocalGalleryItem[]>([
    { id: uuidv4(), before: null, after: null },
  ]);
  const { toast } = useToast();
  const { user } = useAuth();
  // Fetch gallery items using the new API endpoint
  const { data: fetchedGalleryItems, isLoading: isFetching } = useCustomQuery({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.getPresetGalleries,
    parameter: { id: presetId || "" },
    options: {
      enabled: !!presetId && isEditing,
    },
  });

  // Upload gallery item mutation (novo)
  const uploadGalleryItemMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.uploadPresetGalleryItem,
  });

  // Delete gallery item mutation
  const deleteGalleryMutation = useCustomMutation({
    serviceInstance: PresetService.Instance,
    selectAction: (service) => service.deletePresetGallery,
  });

  // Remoção lógica: ids marcados para deleção
  const [galleryItemsToDelete, setGalleryItemsToDelete] = useState<string[]>(
    []
  );
  // Ao remover, só marca para deleção
  const markGalleryItemForDelete = (id: string) => {
    setGalleryItems((prev) => prev.filter((item) => item.id !== id));
    setGalleryItemsToDelete((prev) => [...prev, id]);
  };
  // Commit deletions no backend
  const commitGalleryDeletions = async () => {
    for (const galleryUuid of galleryItemsToDelete) {
      try {
        await deleteGalleryMutation.mutateAsync({ galleryUuid });
      } catch (error) {
        toast({
          title: "Erro ao remover galeria",
          description: "Não foi possível remover a galeria.",
          variant: "destructive",
        });
      }
    }
    setGalleryItemsToDelete([]);
    // Limpa o cache da query de galerias
    queryClient.invalidateQueries({ queryKey: ["get-preset-galleries"] });
  };

  // Update local state when data is fetched
  useEffect(() => {
    if (fetchedGalleryItems && fetchedGalleryItems.length > 0) {
      setGalleryItems(
        fetchedGalleryItems.map((item) => ({
          id: item.id,
          before: item.before,
          after: item.after,
        }))
      );
    }
  }, [fetchedGalleryItems]);

  // Add a new gallery item
  const addGalleryItem = () => {
    setGalleryItems((prev) => [
      ...prev,
      { id: uuidv4(), before: null, after: null },
    ]);
  };


  // Handle gallery photo change - updated parameter order to match the components
  const handleGalleryPhotoChange = async (
    itemId: string,
    type: "before" | "after",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (500KB max)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        title: "Erro ao carregar imagem",
        description: getFileSizeErrorMessage(),
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result as string;
      setGalleryItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, [type]: preview, [`${type}File`]: file }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };

  // Upload all gallery images usando o novo endpoint
  const uploadGalleryImages = async (
    presetId: string
  ): Promise<GalleryItem[]> => {

    if (!presetId) {
      console.error("Preset ID is required to upload gallery images");
      return galleryItems;
    }

    // Só processa itens que tenham arquivos para upload
    const itemsToProcess = galleryItems.filter(
      (item) => item.beforeFile && item.afterFile
    );
    if (itemsToProcess.length === 0) {
      return [];
    }

    try {
      const uploaded: GalleryItem[] = [];
      for (const item of itemsToProcess) {
        const data = {
          presetId,
          before: "",
          after: "",
          createdById: user.id, // Preencha conforme necessário
          displayOrder: 1, // Ajuste conforme necessário
        };
        await uploadGalleryItemMutation
          .mutateAsync({
            beforeFile: item.beforeFile!,
            afterFile: item.afterFile!,
            data,
          })
          .then(() => {
            queryClient.invalidateQueries({
              queryKey: ["get-preset-galleries"],
            });
          });
        uploaded.push({
          id: item.id,
          before: item.before,
          after: item.after,
        });
      }
      return uploaded;
    } catch (error) {
      console.error("Error uploading gallery images:", error);
      toast({
        title: "Erro ao fazer upload das imagens",
        description: "Ocorreu um erro ao fazer upload das imagens da galeria",
        variant: "destructive",
      });
      return [];
    }
  };

  return {
    galleryItems,
    setGalleryItems,
    isLoading: isFetching,
    addGalleryItem,
    removeGalleryItem: markGalleryItemForDelete,
    handleGalleryPhotoChange,
    uploadGalleryImages,
    commitGalleryDeletions,
    galleryItemsToDelete,
  };
};
